import {
	Box,
	Collapse,
	MenuItem,
	Select,
	TextField,
	Typography
} from "@mui/material";
import { useContext, useState } from "react";
import { ReactComponent as CarIcon } from "../../../Assets/Icons/CarIcon.svg";
import { ReactComponent as MailIcon } from "../../../Assets/Icons/EnvelopeIcon.svg";
import { ReactComponent as PhoneIcon } from "../../../Assets/Icons/OldPhoneIcon.svg";
import { ReactComponent as UserIcon } from "../../../Assets/Icons/ProfileIcon.svg";
import { ReactComponent as ProfileIcon } from "../../../Assets/Icons/UserIcon.svg";
import { areaCodesArray } from "../../../Helpers/AreaCodes";
import { ColorScheme } from "../../../Themes/ColorScheme";
import { ApiContext } from "../../ApiContext";
import OnOffSlider from "../../Common/OnOffSlider";
import { Spinner } from "../../Common/Spinner";

import { useTranslation } from "react-i18next";
import { isValidEmail, isValidLicensePlate, isValidName, isValidPhone } from "../../../Helpers/Validation";
import { AlertBar } from "../../Common/AlertBar";
import { TextfieldPicker } from "../../Common/TextfieldPicker";
import { SnackContext } from "../../SnackContext";

const styles = {
	iconContainer: {
		height: "20px",
		minHeight: "20px",
		maxHeight: "20px",
		width: "20px",
		minWidth: "20px",
		maxWidth: "20px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		boxSizing: "border-box",
		marginLeft: "10px"
	},
	inputContainer: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		borderRadius: "4px",
		width: "100%",
		backgroundColor: ColorScheme.lightGrey,
		marginBottom: "10px",
		alignSelf: "center",
		boxSizing: "border-box",
		border: "1px solid " + ColorScheme.lightGrey
	},
	inputHeader: {
		fontSize: "12px",
		color: ColorScheme.primary,
		fontWeight: "700",
		marginTop: 0
	}
};

export const KeyAddAccess = ({ data, handleRefresh, users }) => {
	const { ApiCall } = useContext(ApiContext);
	const { t } = useTranslation();
	const { showSnackMessage } = useContext(SnackContext);
	const [email, setEmail] = useState("");
	const [areaNumber, setAreaNumber] = useState("+358");
	const [phone, setPhone] = useState("");
	const [licensePlate, setLicensePlate] = useState("");
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const [selectedStartTime, setSelectedStartTime] = useState(new Date());
	const [selectedEndTime, setSelectedEndTime] = useState(new Date("2038-01-01T00:00:00Z"));
	const [timeRestrictions, setTimeRestrictions] = useState(false);
	const emailValue = email.trim();
	const phoneValue = phone.trim();
	const licensePlateValue = licensePlate.trim();
	const selectedMode = emailValue !== "" ? "email" : "non-email";

	const handleAreaChange = (event) => {
		setAreaNumber(event.target.value);
	};

	const validateEmail = (email) => {
		const re = /\S+@\S+\.\S+/;
		return re.test(email);
	};

	const handleStartDate = (date) => {
		if (date === null) {
			date = new Date();
			date.setHours(0, 0, 0, 0);
		}
		setSelectedStartTime(date);
	};

	const handleEndDate = (date) => {
		if (date === null) {
			date = new Date("2038-01-01T00:00:00Z");
		}
		setSelectedEndTime(date);
	};

	const switchTimeRestrictions = () => {
		setTimeRestrictions(!timeRestrictions);
	};

	const validateEntry = () => {
		if (selectedMode === "email") return validateEmail(emailValue);
		else {
			if (phoneValue === "" && licensePlateValue === "") {
				return false;
			}
			if (phoneValue !== "" && isValidPhone(areaNumber + phoneValue) === false) {
				return false;
			}
			if (licensePlateValue !== "" && isValidLicensePlate(licensePlateValue) === false) {
				return false;
			}
		}
		return true;
	};

	const isDuplicate = () => {
		if (selectedMode === "email") {
			return users?.some((user) => user.email === emailValue);
		}

		if (phoneValue !== "") {
			const fullPhoneValue = areaNumber + phoneValue;
			if (users?.some((user) => user.phone === fullPhoneValue || user.phone === phoneValue)) {
				return true;
			}
		}

		if (licensePlateValue !== "") {
			const normalizedLicensePlate = licensePlateValue.replace(/-/g, "");
			if (users?.some((user) => user.license_plate === normalizedLicensePlate || user.license_plate === licensePlateValue)) {
				return true;
			}
		}

		return false;
	};

	const handleClick = () => {
		if (validateEntry()) handleSave();
	};

	function isDisabled() {
		if (selectedMode === "email") {
			if (emailValue === "") {
				return t("textfield_labels.add_email");
			}
			if (isValidEmail(emailValue) === false) {
				return t("textfield_labels.email_invalid");
			}
			if (phoneValue != "" && isValidPhone(areaNumber + phoneValue) === false) {
				return t("textfield_labels.phone_number_invalid");
			}
			if (
				licensePlateValue !== "" &&
				isValidLicensePlate(licensePlateValue) === false
			) {
				return t("textfield_labels.license_plate_invalid");
			}
			if (name != "" && isValidName(name) === false) {
				return t("textfield_labels.name_invalid");
			}
		} else {
			if (phoneValue === "" && licensePlateValue === "") {
				return t("textfield_labels.add_email_phone_or_license_plate");
			}
			if (phoneValue != "" && isValidPhone(areaNumber + phoneValue) === false) {
				return t("textfield_labels.phone_number_invalid");
			}
			if (
				licensePlateValue !== "" &&
				isValidLicensePlate(licensePlateValue) === false
			) {
				return t("textfield_labels.license_plate_invalid");
			}
			if (name != "" && isValidName(name) === false) {
				return t("textfield_labels.name_invalid");
			}
		}

		return "";
	}

	const handleSave = () => {
		setIsLoading(true);
		ApiCall("POST", "client/residents/" + data.id , [
			{
				email: emailValue,
				phone: phoneValue !== "" ? areaNumber + phoneValue : "",
				license_plate: licensePlateValue.replace(/-/g, ""), // Remove "-" characters from license plate
				description,
				name: name.trim(),
				...(selectedStartTime !== null &&
					timeRestrictions && { from: selectedStartTime }),
				...(selectedEndTime !== null &&
					timeRestrictions && { to: selectedEndTime })
			}
		])
			.then(() => {
				resetValues();
				handleRefresh();

				showSnackMessage("general", 200);
			})
			.catch(() => {
				/* setShowError(true); */
				// console.log(error);
			})
			.finally(() => setIsLoading(false));
	};

	function resetValues() {
		setEmail("");
		setDescription("");
		setLicensePlate("");
		setName("");
		setPhone("");
		setSelectedStartTime(new Date());
		setSelectedEndTime("2038-01-01T00:00:00Z");
	}

	return (
		<>
			<div style={{ width: "100%", backgroundColor: ColorScheme.secondary }}>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							borderTop: `1px solid ${ColorScheme.lightGrey}`,
							padding: "20px"
						}}
					>
						{
							<AlertBar
								textContent={t("key_management.add_non_tologo_user")}
								type="info"
								showAlert={true}
								standardVariant={"standard"}
							/>
						}
						{1 + data.user_count > data.user_limit && (
							<AlertBar
								textContent={t("key.no_space_for_new_users")}
								type="error"
								showAlert={true}
								standardVariant={"standard"}
							></AlertBar>
						)}
						{1 + data.user_count <= data.user_limit && (
								<>
									<Box style={styles.inputContainer}>
										<Box style={styles.iconContainer}>
											<MailIcon
												stroke={
													ColorScheme.primary
												}
												strokeWidth="1.7px"
											/>
										</Box>
										<TextField
											on
											variant="filled"
											label={t("textfield_labels.email")}
											placeholder={t(
												"textfield_labels.input_email"
											)}
											sx={{
												border: "none",
												width: "100%",
												flex: 1
											}}
											InputProps={{
												disableUnderline: true,
												style: {
													color: ColorScheme.black,
													backgroundColor:
														"transparent",
													borderRadius: "20px",
													border: "0px solid", 
													fontSize: "15px"
												}
											}}
											InputLabelProps={{
												style: {
													fontSize: "14px",
													top: "3px"
												}
											}}
											value={email}
											onChange={(event) => {
												let inputValue =
													event.target.value;
												setEmail(inputValue);
											}}
										/>
									</Box>
								</>
							)}

						{1 + data.user_count <= data.user_limit && (
								<>
									<Box style={styles.inputContainer}>
										<Box style={styles.iconContainer}>
											<ProfileIcon
												stroke={
													ColorScheme.primary
												}
												strokeWidth="2px"
											/>
										</Box>
										<TextField
											label={t("textfield_labels.name")}
											variant="filled"
											placeholder={t(
												"textfield_labels.input_name"
											)}
											sx={{
												border: "none",
												width: "100%"
											}}
											InputProps={{
												disableUnderline: true,
												style: {
													color: ColorScheme.black,
													backgroundColor:
														"transparent",
													borderRadius: "20px",
													border: "0px solid",
													fontSize: "15px"
												}
											}}
											InputLabelProps={{
												style: {
													fontSize: "14px",
													top: "3px"
												}
											}}
											value={name}
											onChange={(event) => {
												let inputValue =
													event.target.value;
												setName(inputValue);
											}}
										/>
									</Box>

									<Box style={styles.inputContainer}>
										<Box style={styles.iconContainer}>
											<PhoneIcon
												stroke={
													ColorScheme.primary
												}
												strokeWidth="1.7px"
											/>
										</Box>
										<Select
											value={areaNumber}
											label={
												areaNumber ? "" : "Suuntanumero"
											}
											onChange={handleAreaChange}
											sx={{
												height: "30px",
												marginLeft: "10px"
											}}
											renderValue={() => areaNumber}
										>
											{areaCodesArray.map((areaCode) => (
												<MenuItem
													key={areaCode.code}
													value={areaCode.code}
												>
													{areaCode.country +
														" " +
														areaCode.code}
												</MenuItem>
											))}
										</Select>
										<TextField
											label={t("textfield_labels.phone")}
											variant="filled"
											placeholder={t(
												"textfield_labels.input_phone"
											)}
											notched={false}
											sx={{
												border: "none",
												width: "100%"
											}}
											InputProps={{
												disableUnderline: true,
												style: {
													color: ColorScheme.black,
													backgroundColor:
														"transparent",
													borderRadius: "20px",
													border: "0px solid",
													fontSize: "15px"
												}
											}}
											InputLabelProps={{
												style: {
													fontSize: "14px",
													top: "3px"
												}
											}}
											value={phone}
											onChange={(event) => {
												let inputValue =
													event.target.value;
												setPhone(inputValue);
											}}
										/>
									</Box>

									<Box style={styles.inputContainer}>
										<Box style={styles.iconContainer}>
											<CarIcon
												stroke={
													ColorScheme.primary
												}
												strokeWidth="1.7px"
											/>
										</Box>
										<TextField
											label={t(
												"textfield_labels.license_plate"
											)}
											variant="filled"
											placeholder={t(
												"textfield_labels.input_license_plate"
											)}
											sx={{
												border: "none",
												width: "100%"
											}}
											InputProps={{
												disableUnderline: true,
												style: {
													color: ColorScheme.black,
													backgroundColor:
														"transparent",
													borderRadius: "20px",
													border: "0px solid",
													fontSize: "15px"
												}
											}}
											InputLabelProps={{
												style: {
													fontSize: "14px",
													top: "3px"
												}
											}}
											value={licensePlate}
											onChange={(event) => {
												let inputValue =
													event.target.value;
												setLicensePlate(inputValue);
											}}
										/>
									</Box>

									<Box style={styles.inputContainer}>
										<Box style={styles.iconContainer}>
											<UserIcon
												stroke={
													ColorScheme.primary
												}
												strokeWidth="1.7px"
											/>
										</Box>
										<TextField
											label={t(
												"textfield_labels.description"
											)}
											variant="filled"
											placeholder={t(
												"textfield_labels.input_description"
											)}
											sx={{
												width: "100%"
												/* px: "15px", */
											}}
											InputProps={{
												disableUnderline: true,
												style: {
													color: ColorScheme.black,
													backgroundColor:
														"transparent",
													borderRadius: "0px",
													fontSize: "15px"
												}
											}}
											InputLabelProps={{
												style: {
													fontSize: "14px",
													top: "3px"
												}
											}}
											value={description}
											onChange={(text) =>
												setDescription(
													text.target.value
												)
											}
										/>
									</Box>
								</>
							)}
						{1 + data.user_count <= data.user_limit && (
							<>
								<div
									style={{
										display: "flex",
										flexDirection: "row",
										alignItems: "center",
										justifyContent: "space-between",
										backgroundColor: ColorScheme.secondary,
										height: "50px",
									}}
								>
									<Typography
										variant="form_bold"
										fontSize={14}
										color={ColorScheme.black}
									>
										{t("key.optional_time_restrictions")}
									</Typography>
										<OnOffSlider
											status={timeRestrictions}
											action={switchTimeRestrictions}
										/>
								</div>
								{
									<Collapse in={timeRestrictions}>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												marginBottom: "20px"
											}}
										>
											<TextfieldPicker
												maskUnlimitedStart={true}
												noTime={true}
												removeIcon={true}
												value={selectedStartTime}
												excludePast={true}
												label={t("textfield_labels.start_date")}
												fullDate={false}
												leanToEndOfDay={false}
												valueChange={handleStartDate}
											/>
											<div
												style={{ width: "10px" }}
											></div>
											<Typography sx={{ color: ColorScheme.black }}>
												{" - "}
											</Typography>
											<div
												style={{ width: "10px" }}
											></div>

											<TextfieldPicker
												maskUnlimitedEnd={true}
												noTime={true}
												removeIcon={true}
												value={selectedEndTime}
												excludePast={true}
												label={t("textfield_labels.end_date")}
												fullDate={false}
												valueChange={handleEndDate}
												leanToEndOfDay={true}
											/>
										</div>
									</Collapse>
								}
								<div
									style={{
										width: "100%",
										height: "50px",
										boxSizing: "border-box",
										display: "flex",
										alignItems: "center",
										justifyContent: "center"
									}}
								>
									<div
										style={{
											width: "100%",
											height: "100%",
											textAlign: "center",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											borderRadius: "4px",
											transition:
												"background-color 0.3s ease-in-out",
											backgroundColor:
												ColorScheme.primary,
											opacity:
												isDisabled() !== "" ? 0.5 : 1,
											cursor:
												isDisabled() !== ""
													? "cursor"
													: "pointer"
										}}
										onClick={
											isDisabled() !== ""
												? undefined
												: handleClick
										}
									>
										{isLoading ? (
											<Spinner color={ColorScheme.secondary} small />
										) : isDuplicate() ? (
											<Typography>
												{t(
													selectedMode === "email"
														? "textfield_labels.email_in_use"
														: "textfield_labels.phone_in_use"
												)}
											</Typography>
										) : (
											<Typography
												sx={{
													color:
														isDisabled() !== ""
															? ColorScheme.secondary
															: ColorScheme.secondary
												}}
											>
												{isDisabled() !== ""
													? isDisabled()
													: t("buttons.save")}
											</Typography>
										)}
									</div>
								</div>
							</>
						)}
					</div>
			</div>
		</>
	);
};
